import {FC} from 'react';

import {ScheduleI} from '~src/interfaces/schedule.interface';
import {Segment} from '~src/interfaces/segment.interface';
import {SurveySubjectTypes, SurveyUseCase} from '~src/interfaces/surveys.interfaces';
import {fetchSearchSegments} from '~src/services/fetch/fetchSearchSegments.service';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import {Survey, SurveyScopes} from '~src/modules/surveys/interfaces';
import {
  ChipsError,
  HeaderContainer,
  PeopleSectionHeader,
  PeopleSectionManagerVisibility,
  PeopleSectionManagerVisibilityLabel,
  PeopleSectionManagerVisibilitySlider,
  SchedulesItemSection,
  SectionLine,
  TopicDescription,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';

import PeopleChips from './customInputs/PeopleChips';
import DoubleCheckBoxSection from './DoubleCheckBoxSection';
import {useTranslation} from "react-i18next";
import {
  SectionPart
} from "~src/modules/surveys/components/schedules/scheduleItem/templates/sections/styles/SectionStyles";
import Checkbox from "~src/shared/form/Checkbox";
import {Alert, Slider} from "antd";
import {useCompany} from "~src/services/hooks/useCompany";

interface Props {
  schedule: ScheduleI;
  changeBoolean: (val: boolean, key: keyof ScheduleI) => void;
  changeManagersLevels: (val: number) => void;
  changeAttributesVisibility: (val: boolean, key: keyof ScheduleI) => void;
  changeShowInHeatmapToprow: (val: boolean, key: keyof ScheduleI) => void;
  survey: Survey;
  deleteError: (err: string) => void;
  getSharedPeople: (people: Segment[], key: string) => void;
  error: boolean;
  useCaseKey: string;
}

const ShareSection: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const adminsCanViewManagerSurveys = useCompany()?.features.survey?.adminsCanViewManagerSurveys;


  const {
    error,
    survey: { useCase, subject },
  } = props;
  const useCaseKey = useCase !== SurveyUseCase.ThreeSixty ? `.${useCase}` : '';
  const changeSelfSelect = (isChecked: boolean) => {
    props.changeBoolean(isChecked, 'shareWithSubject');
  };
  const changeAttributesVisibility = (isChecked: boolean) => {
    props.changeAttributesVisibility(isChecked, 'attributesVisibility');
  };

  const changeManagerSelect = (value: number) => {
    props.changeManagersLevels(value);
  };

  return (
    <SchedulesItemSection>
      <SectionLine>
        <PeopleSectionHeader>
          <HeaderContainer data-translation="x.surveys.schedule.visibilitysection.header">
            {t('x.surveys.schedule.visibilitysection.header')}
            {props.survey.scope === SurveyScopes.Admin && (
              <TopicDescription>
                <HoverInfoIcon
                  infoText={{
                    key: `x.surveys.schedule.asksection.header.info_box.sharesection.${subject}${props.useCaseKey}`,
                    txt: t(
                      `x.surveys.schedule.asksection.header.info_box.sharesection.${subject}${props.useCaseKey}`,
                    ),
                  }}
                />
              </TopicDescription>
            )}
          </HeaderContainer>
          {error && (
            <ChipsError data-translation="x.surveys.schedule.asksection.errormessage">
              {t('x.surveys.schedule.asksection.errormessage')}
            </ChipsError>
          )}
        </PeopleSectionHeader>
      </SectionLine>
      <SectionLine $noPadding>
        <PeopleChips
          locked={SurveyScopes.Manager === props.survey.scope}
          fetchSuggestions={fetchSearchSegments}
          chipsName="share"
          selectedPeople={props.schedule.share || []}
          onChangePeople={props.getSharedPeople}
          error={error}
          deleteError={props.deleteError}
        />
      </SectionLine>
      {
        adminsCanViewManagerSurveys && (props.survey.scope === SurveyScopes.Manager) &&
        <Alert type={'info'} message={t('x.surveys.schedule.visibilitysection.admin-can-view-manager-surveys-hint')} showIcon={true}></Alert>
      }
      <SectionLine>
        {subject !== SurveySubjectTypes.Groups &&
          subject !== SurveySubjectTypes.General &&
          props.survey.scope !== SurveyScopes.User && <PeopleSectionHeader>
              <SectionPart>
                <Checkbox
                  id="answerVisiblePerson"
                  label={t(`x.surveys.schedule.visibilitysection.checkbox.one.title${useCaseKey}`)}
                  onChange={changeSelfSelect}
                  isChecked={props.schedule.shareWithSubject}
                />
              </SectionPart>
            </PeopleSectionHeader>}
      </SectionLine>
      <SectionLine>
        {subject !== SurveySubjectTypes.Groups &&
          subject !== SurveySubjectTypes.General &&
          props.survey.scope !== SurveyScopes.User && <PeopleSectionManagerVisibility>
            <PeopleSectionManagerVisibilityLabel>
              {t(`x.surveys.schedule.visibilitysection.checkbox.two.title${useCaseKey}`)}
            </PeopleSectionManagerVisibilityLabel>
            <PeopleSectionManagerVisibilitySlider>
              <Slider
                range={false}
                value={props.schedule.shareWithManagersLevels}
                min={props.survey.scope === SurveyScopes.Manager ? 1 : 0}
                max={2}
                marks={{
                  0: t("x.surveys.schedule.visibilitysection.managers.no"),
                  1: t("x.surveys.schedule.visibilitysection.managers.1level"),
                  2: t("x.surveys.schedule.visibilitysection.managers.2levels")
                }}
                onChange={changeManagerSelect}
              />
            </PeopleSectionManagerVisibilitySlider>
          </PeopleSectionManagerVisibility>}
      </SectionLine>
      <SectionLine>
        {((subject === SurveySubjectTypes.Groups && props.survey.scope !== SurveyScopes.User) ||
          subject === SurveySubjectTypes.General) && (
          <DoubleCheckBoxSection
            id1="attributesVisibility"
            titleOne={t('x.surveys.schedule.visibilitysection.attributes')}
            onChangeCheckboxOne={changeAttributesVisibility}
            selectedValueOne={props.schedule.attributesVisibility}
            help1={t('x.surveys.schedule.visibilitysection.attributes.help')}
          />
        )}
      </SectionLine>
      {/*
      // todo see LN-208 (child of LN-48) for next steps in regards to the heatmap top row
      <SectionLine>
        {(subject === SurveySubjectTypes.Groups || subject === SurveySubjectTypes.General) && (
          <DoubleCheckBoxSection
            id1="showInHeatmapToprow"
            titleOne={t('x.surveys.schedule.show_in_heatmap_toprow.check')}
            onChangeCheckboxOne={showInHeatmapToprow}
            selectedValueOne={props.schedule.showInHeatmapToprow}
            help1={t('x.surveys.schedule.show_in_heatmap_toprow.help')}
          />
        )}
      </SectionLine>
*/}
    </SchedulesItemSection>
  );
};

export default ShareSection;
